/**
 * Project Dashboard page — lists all projects in a card grid.
 * See wireframe: section 3 Project Dashboard
 */

import { router } from '@/router/router';
import { getCurrentUser } from '@/api/apiClient';

export function DashboardPage(): HTMLElement {
  const page = document.createElement('div');
  page.className = 'dashboard-page';
  page.style.height = '100%';
  page.style.display = 'flex';
  page.style.flexDirection = 'column';

  page.innerHTML = `
    <!-- Top Bar -->
    <header class="topbar">
      <a class="topbar-logo" href="#/dashboard">RoofmAIte</a>
      <span class="topbar-title">Projects</span>
      <div class="topbar-actions">
        <div class="user-menu" id="user-menu">
          <button class="btn btn-ghost user-menu-btn" id="user-menu-btn" aria-haspopup="true" aria-expanded="false">
            <span id="user-name">Loading…</span>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
          <div class="dropdown-menu" id="user-dropdown" hidden>
            <a class="dropdown-item" href="#/settings">Settings</a>
            <button class="dropdown-item dropdown-item-danger" id="logout-btn">Log Out</button>
          </div>
        </div>
        <a class="btn btn-ghost btn-icon" href="#/settings" title="Settings" aria-label="Settings">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </a>
      </div>
    </header>

    <!-- Action Bar -->
    <div class="action-bar">
      <button class="btn btn-primary" id="new-project-btn">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
        </svg>
        New Project
      </button>
      <div class="action-bar-right">
        <input
          type="search"
          class="search-input"
          id="search-input"
          placeholder="Search projects…"
          aria-label="Search projects"
        />
        <select class="sort-select" id="sort-select" aria-label="Sort projects">
          <option value="updatedAt-desc">Last Modified</option>
          <option value="name-asc">Name (A–Z)</option>
          <option value="name-desc">Name (Z–A)</option>
          <option value="createdAt-desc">Date Created</option>
        </select>
      </div>
    </div>

    <!-- Project Grid -->
    <main class="dashboard-main" id="projects-container">
      <div class="projects-loading" id="projects-loading">
        <div class="spinner spinner-lg"></div>
      </div>
      <div class="projects-grid" id="projects-grid" hidden></div>
      <div class="projects-empty" id="projects-empty" hidden>
        <div class="empty-icon" aria-hidden="true">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
        </div>
        <h3>No projects yet</h3>
        <p>Create your first project to get started.</p>
        <button class="btn btn-primary" id="empty-new-project-btn">
          Create Project
        </button>
      </div>
    </main>

    <!-- New Project Dialog (hidden) -->
    <div class="modal-backdrop" id="new-project-modal" hidden role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div class="modal">
        <div class="modal-header">
          <h2 id="modal-title">New Project</h2>
          <button class="btn btn-ghost btn-icon" id="close-modal-btn" aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>

        <form id="new-project-form" novalidate>
          <div class="form-group">
            <label for="project-name">Project Name</label>
            <input type="text" id="project-name" name="name" required maxlength="200" />
            <span class="text-danger" id="project-name-error"></span>
          </div>
          <div class="form-group">
            <label for="client-name">Client Name (optional)</label>
            <input type="text" id="client-name" name="clientName" maxlength="200" />
          </div>

          <fieldset class="start-method-group">
            <legend>How would you like to start?</legend>
            <div class="start-method-cards">
              <label class="start-method-card" id="satellite-card">
                <input type="radio" name="startMethod" value="satellite" checked class="sr-only" />
                <div class="start-method-icon" aria-hidden="true">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="3" />
                    <path stroke-linecap="round" d="M12 2v3M12 19v3M2 12h3M19 12h3" />
                  </svg>
                </div>
                <strong>Trace from Satellite Image</strong>
              </label>

              <label class="start-method-card" id="scratch-card">
                <input type="radio" name="startMethod" value="scratch" class="sr-only" />
                <div class="start-method-icon" aria-hidden="true">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </div>
                <strong>Draw from Scratch</strong>
              </label>
            </div>
          </fieldset>

          <span class="text-danger" id="modal-form-error"></span>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" id="cancel-modal-btn">Cancel</button>
            <button type="submit" class="btn btn-primary" id="create-project-btn">Create Project</button>
          </div>
        </form>
      </div>
    </div>
  `;

  attachDashboardHandlers(page);
  loadProjects(page);
  loadCurrentUser(page);

  return page;
}

// ============================================================
// Handlers
// ============================================================

function loadCurrentUser(page: HTMLElement): void {
  const user = getCurrentUser();
  const nameEl = page.querySelector<HTMLElement>('#user-name');
  if (nameEl && user) nameEl.textContent = user.name;
}

async function loadProjects(page: HTMLElement): Promise<void> {
  const loading = page.querySelector<HTMLElement>('#projects-loading')!;
  const grid = page.querySelector<HTMLElement>('#projects-grid')!;
  const empty = page.querySelector<HTMLElement>('#projects-empty')!;

  try {
    const { projectsApi } = await import('@/api/projectsApi');
    const { items } = await projectsApi.list({ sortBy: 'updatedAt', sortDir: 'desc' });

    loading.hidden = true;

    if (items.length === 0) {
      empty.hidden = false;
    } else {
      grid.hidden = false;
      renderProjectGrid(grid, items);
    }
  } catch {
    loading.hidden = true;
    empty.hidden = false;
  }
}

function renderProjectGrid(
  grid: HTMLElement,
  items: import('@/types').ProjectSummaryDto[],
): void {
  grid.innerHTML = items.map(project => `
    <div class="project-card" data-project-id="${project.id}" role="button" tabindex="0" aria-label="Open ${project.name}">
      <div class="project-thumbnail">
        ${project.thumbnailUrl
          ? `<img src="${project.thumbnailUrl}" alt="" />`
          : '<div class="project-thumbnail-placeholder"></div>'
        }
      </div>
      <div class="project-card-body">
        <h3 class="project-card-name">${escapeHtml(project.name)}</h3>
        ${project.clientName ? `<p class="project-card-client">Client: ${escapeHtml(project.clientName)}</p>` : ''}
        <p class="project-card-date">${formatRelativeTime(project.updatedAt)}</p>
      </div>
      <button class="btn btn-ghost btn-icon project-card-menu" data-project-id="${project.id}" aria-label="More options for ${project.name}" title="More options">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
        </svg>
      </button>
    </div>
  `).join('');

  // Open project on card click
  grid.querySelectorAll<HTMLElement>('.project-card').forEach(card => {
    const projectId = card.dataset['projectId']!;
    card.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('.project-card-menu')) return;
      router.navigate(`/editor/${projectId}`);
    });
    card.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        router.navigate(`/editor/${projectId}`);
      }
    });
  });
}

function attachDashboardHandlers(page: HTMLElement): void {
  const modal = page.querySelector<HTMLElement>('#new-project-modal')!;
  const form = page.querySelector<HTMLFormElement>('#new-project-form')!;
  const projectNameInput = page.querySelector<HTMLInputElement>('#project-name')!;
  const projectNameError = page.querySelector<HTMLSpanElement>('#project-name-error')!;
  const modalFormError = page.querySelector<HTMLSpanElement>('#modal-form-error')!;
  const createBtn = page.querySelector<HTMLButtonElement>('#create-project-btn')!;

  // Open modal
  const openModal = () => { modal.hidden = false; projectNameInput.focus(); };
  const closeModal = () => { modal.hidden = true; form.reset(); projectNameError.textContent = ''; };

  page.querySelector('#new-project-btn')?.addEventListener('click', openModal);
  page.querySelector('#empty-new-project-btn')?.addEventListener('click', openModal);
  page.querySelector('#close-modal-btn')?.addEventListener('click', closeModal);
  page.querySelector('#cancel-modal-btn')?.addEventListener('click', closeModal);

  // Close on backdrop click
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });

  // Highlight selected start method card
  page.querySelectorAll<HTMLInputElement>('input[name="startMethod"]').forEach(radio => {
    radio.addEventListener('change', () => {
      page.querySelectorAll('.start-method-card').forEach(c => c.classList.remove('selected'));
      radio.closest('.start-method-card')?.classList.add('selected');
    });
  });
  // Set initial selected state
  page.querySelector<HTMLElement>('#satellite-card')?.classList.add('selected');

  // User dropdown toggle
  const userMenuBtn = page.querySelector<HTMLButtonElement>('#user-menu-btn')!;
  const userDropdown = page.querySelector<HTMLElement>('#user-dropdown')!;
  userMenuBtn.addEventListener('click', () => {
    const expanded = userMenuBtn.getAttribute('aria-expanded') === 'true';
    userMenuBtn.setAttribute('aria-expanded', String(!expanded));
    userDropdown.hidden = expanded;
  });

  // Logout
  page.querySelector('#logout-btn')?.addEventListener('click', async () => {
    const { logout } = await import('@/api/apiClient');
    logout();
    router.replace('/login');
  });

  // Create project form
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    projectNameError.textContent = '';
    modalFormError.textContent = '';

    const name = projectNameInput.value.trim();
    const clientName = page.querySelector<HTMLInputElement>('#client-name')!.value.trim() || undefined;
    const startMethod = page.querySelector<HTMLInputElement>('input[name="startMethod"]:checked')!.value;

    if (!name) {
      projectNameError.textContent = 'Project name is required.';
      return;
    }

    createBtn.disabled = true;
    createBtn.textContent = 'Creating…';

    try {
      const { projectsApi } = await import('@/api/projectsApi');
      const project = await projectsApi.create({ name, clientName });

      closeModal();

      if (startMethod === 'satellite') {
        router.navigate(`/satellite/${project.id}`);
      } else {
        router.navigate(`/editor/${project.id}`);
      }
    } catch (err) {
      modalFormError.textContent =
        err instanceof Error ? err.message : 'Failed to create project.';
    } finally {
      createBtn.disabled = false;
      createBtn.textContent = 'Create Project';
    }
  });

  // Search
  const searchInput = page.querySelector<HTMLInputElement>('#search-input')!;
  let searchTimeout: ReturnType<typeof setTimeout>;
  searchInput.addEventListener('input', () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => reloadProjects(page), 300);
  });

  // Sort
  page.querySelector('#sort-select')?.addEventListener('change', () => reloadProjects(page));
}

async function reloadProjects(page: HTMLElement): Promise<void> {
  const grid = page.querySelector<HTMLElement>('#projects-grid')!;
  const empty = page.querySelector<HTMLElement>('#projects-empty')!;
  const search = page.querySelector<HTMLInputElement>('#search-input')!.value.trim() || undefined;
  const sortValue = page.querySelector<HTMLSelectElement>('#sort-select')!.value;
  const [sortBy, sortDir] = sortValue.split('-') as [
    'name' | 'createdAt' | 'updatedAt',
    'asc' | 'desc',
  ];

  try {
    const { projectsApi } = await import('@/api/projectsApi');
    const { items } = await projectsApi.list({ search, sortBy, sortDir });

    if (items.length === 0) {
      grid.hidden = true;
      empty.hidden = false;
    } else {
      empty.hidden = true;
      grid.hidden = false;
      renderProjectGrid(grid, items);
    }
  } catch {
    // Keep existing content on error
  }
}

// ============================================================
// Utilities
// ============================================================

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatRelativeTime(dateStr: string): string {
  const ms = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return '1 week ago';
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} month${Math.floor(days / 30) > 1 ? 's' : ''} ago`;
}
